import React, { useState } from 'react';
import { Bell, Loader2, CheckCircle2 } from 'lucide-react';
import { directoryApi } from '../../../lib/api';
import { trackGenerateLead } from '../../../lib/analytics';

export const RaceAlertCapture = ({ cityId, cityLabel, variant = 'section', eventNote = '' }) => {
  const storageKey = `race_alert_${cityId}`;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(() => localStorage.getItem(storageKey) === '1');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await directoryApi.subscribeRaceAlerts({ email, city: cityId });
      localStorage.setItem(storageKey, '1');
      trackGenerateLead('race_alert', cityId);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'banner') {
    return (
      <section className="mb-10 p-4 sm:p-5 bg-[#C7FA3E]/[0.06] border border-[#C7FA3E]/40" data-testid={`race-alert-banner-${cityId}`}>
        {done ? (
          <div className="flex items-center gap-3" data-testid="race-alert-banner-success">
            <CheckCircle2 className="w-5 h-5 text-[#C7FA3E] flex-shrink-0" />
            <p className="text-zinc-300 text-sm">You're on the list — check your inbox for {cityLabel}'s top gyms + upcoming event invites.</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5">
            <div className="flex items-start gap-2.5 flex-1">
              <Bell className="w-4 h-4 text-[#C7FA3E] flex-shrink-0 mt-0.5 animate-pulse" />
              <p className="text-white text-sm leading-snug">
                <span className="text-[#C7FA3E] font-bold uppercase tracking-wider text-xs mr-2" style={{ fontFamily: 'Oswald, sans-serif' }}>Event Alert</span>
                {eventNote || `Get HYROX ${cityLabel} race-date alerts:`}
              </p>
            </div>
            <form onSubmit={submit} className="flex gap-2 w-full lg:w-auto">
              <input
                required
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 lg:w-56 px-3 py-2.5 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#C7FA3E] focus:outline-none transition-colors"
                data-testid="race-alert-banner-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#C7FA3E] hover:bg-[#b5e838] text-black text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50 whitespace-nowrap"
                style={{ fontFamily: 'Oswald, sans-serif' }}
                data-testid="race-alert-banner-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Notify Me
              </button>
            </form>
            {error && <p className="text-[#E63946] text-xs" data-testid="race-alert-banner-error">{error}</p>}
          </div>
        )}
      </section>
    );
  }

  return (
    <section id="race-alerts" className="mb-14 p-6 sm:p-8 bg-[#C7FA3E]/[0.04] border border-[#C7FA3E]/25 scroll-mt-28" data-testid={`race-alert-capture-${cityId}`}>
      {done ? (
        <div className="flex items-center gap-3" data-testid="race-alert-success">
          <CheckCircle2 className="w-6 h-6 text-[#C7FA3E] flex-shrink-0" />
          <div>
            <p className="text-white font-semibold text-sm">You're on the list.</p>
            <p className="text-zinc-400 text-xs">Check your inbox — we just sent you {cityLabel}'s top training gyms. Race dates, gym deals and prep-block reminders will follow.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-1.5">
            <Bell className="w-4 h-4 text-[#C7FA3E]" />
            <h2 className="text-white text-2xl sm:text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
              Get HYROX {cityLabel} Race Alerts
            </h2>
          </div>
          <p className="text-zinc-400 text-sm mb-5 max-w-2xl">
            Race dates, registration windows, local gym deals and 12-week prep-block reminders — straight to your inbox. No spam, unsubscribe anytime.
          </p>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-lg">
            <input
              required
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#C7FA3E] focus:outline-none transition-colors"
              data-testid="race-alert-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C7FA3E] hover:bg-[#b5e838] text-black text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid="race-alert-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Notify Me
            </button>
          </form>
          {error && <p className="text-[#E63946] text-xs mt-2" data-testid="race-alert-error">{error}</p>}
        </>
      )}
    </section>
  );
};
